import { config } from '@/config';
import {
  Modal,
  Calendar,
  List,
  Button,
  Spin,
  Select,
  Typography,
  Grid,
  message,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import { CustomButton } from '@/components/ui';
import { useQuery } from '@tanstack/react-query';
import { getAvailability } from '@/api/booking';
import { AvailabilityResponse } from '@/types/booking';
import { convertRuleTimezone } from '@/helpers/dateTimeHelper';
import './BookSlotModal.scss';
dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const { Title } = Typography;

const dayMap: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const TIMEZONES = [
  // Europe
  'Europe/London',

  // United States
  'America/New_York', // Eastern Time
  'America/Chicago', // Central Time
  'America/Denver', // Mountain Time
  'America/Los_Angeles', // Pacific Time
  'America/Anchorage', // Alaska Time
  'Pacific/Honolulu', // Hawaii Time

  // Canada
  'America/Toronto', // Eastern Time
  'America/Winnipeg', // Central Time
  'America/Edmonton', // Mountain Time
  'America/Vancouver', // Pacific Time
  'America/Halifax', // Atlantic Time
  'America/St_Johns', // Newfoundland Time,

  // Australia / New Zealand
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Pacific/Auckland', // NZST/NZDT (New Zealand)
  'Pacific/Chatham', // CHAST/CHADT (Chatham Islands)
];

type Slot = {
  start: string;
  end: string;
};

type AvailabilityDay = {
  date: string;
  slots: Slot[];
};

type BookSlotModalProps = {
  consultantId: string;
  visible: boolean;
  availability: { days: AvailabilityDay[] };
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  loading: boolean;
  onClose: () => void;
  onSlotSelect: (slot: Slot) => void;
  isBooking: boolean;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  setAvailability: (availability: any) => void;
  // Footer button labels, overridable so this modal can be reused for flows
  // other than the initial booking (e.g. reschedule). Defaults preserve the
  // original booking-flow copy for existing callers.
  primaryLabel?: string;
  secondaryLabel?: string;
  // Optional note rendered above the footer buttons (e.g. the reschedule
  // flow's fee-assignment caveat). Omitted by existing callers, so the
  // original booking flow is unaffected.
  footerNote?: React.ReactNode;
};

function BookSlotModal({
  consultantId,
  visible,
  availability,
  selectedDate,
  setSelectedDate,
  loading,
  onClose,
  onSlotSelect,
  isBooking,
  timezone,
  onTimezoneChange,
  setAvailability,
  primaryLabel = 'Schedule Meeting',
  secondaryLabel = 'CANCEL',
  footerNote,
}: BookSlotModalProps) {
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [allSlots, setAllSlots] = useState<Slot[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreSlots, setHasMoreSlots] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [renderDate, setRenderDate] = useState([dayjs().tz(timezone)]);
  // Monotonic token bumped on every fetchInitial run (date/tz/consultant
  // change). handleLoadMore captures it at call time and only commits its
  // appended slots if the token still matches — so a Load More that resolves
  // after the user switched date/tz/consultant can't append stale slots onto
  // the new date's (reset) list. TCX-793.
  const requestGenerationRef = useRef(0);
  const { lg } = Grid.useBreakpoint();

  const { data: availabilityData } = useQuery<AvailabilityResponse>({
    queryKey: ['availability', consultantId],
    queryFn: () => getAvailability(consultantId),
  });

  const { availability_rule } = availabilityData || {};

  const availabilityTimezone = convertRuleTimezone(availability_rule, timezone);

  useEffect(() => {
    setSelectedSlot(null);
  }, [selectedDate, timezone, consultantId]);

  const dateCellRender = (date: Dayjs) => {
    const isAvailable = availability?.days?.some(
      (d) => d.date === date.format('YYYY-MM-DD'),
    );
    const today = dayjs().tz(timezone).startOf('day');
    const isSelected = selectedDate === date.tz(timezone).format('YYYY-MM-DD');
    const isPreviousDay = date.tz(timezone).isBefore(today, 'day');

    return (
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background:
            isAvailable && !isPreviousDay && !loadingSlots
              ? '#2A4BC2'
              : isAvailable
              ? '#e6edfd'
              : undefined,
          color:
            isAvailable && !isPreviousDay && !loadingSlots ? '#fff' : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 'auto',
          fontWeight: 500,
        }}
      >
        {date.date()}
      </div>
    );
  };

  const calendarHeaderRender = ({
    value,
    onChange,
  }: {
    value: Dayjs;
    onChange: (v: Dayjs) => void;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
      }}
    >
      <Button
        type={'text'}
        onClick={() => onChange(value.clone().subtract(1, 'month'))}
        style={{ fontSize: 20, color: '#222', marginRight: 16 }}
        icon={
          <span style={{ display: 'inline-block', transform: 'scale(1.5)' }}>
            {'<'}
          </span>
        }
      />
      <span
        style={{
          fontWeight: 600,
          fontSize: 18,
          color: '#222',
          minWidth: 120,
          textAlign: 'center',
        }}
      >
        {value.format('MMMM YYYY')}
      </span>
      <Button
        type={'text'}
        onClick={() => onChange(value.clone().add(1, 'month'))}
        style={{ fontSize: 20, color: '#222', marginLeft: 16 }}
        icon={
          <span style={{ display: 'inline-block', transform: 'scale(1.5)' }}>
            {'>'}
          </span>
        }
      />
    </div>
  );

  const disabledDate = (date: Dayjs) => {
    const todayTz = dayjs().tz(timezone).startOf('day');
    const dateTz = date.tz(timezone).startOf('day');

    if (dateTz.isBefore(todayTz, 'day')) return true;

    const isAvailable = availabilityTimezone?.weekly_periods?.some((rule) => {
      // Convert "monday" to 1 via the map
      const ruleDayNumber = dayMap[rule.day.toLowerCase()];
      return dateTz.day() === ruleDayNumber;
    });

    return !isAvailable;
  };

  const calendarValue = selectedDate
    ? dayjs.tz(selectedDate, timezone)
    : dayjs().tz(timezone);
  const dedupeSlots = (slots: Slot[]): Slot[] => {
    const seen = new Set();
    return slots.filter((slot) => {
      const key = slot.start + '|' + slot.end;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  useEffect(() => {
    setAllSlots([]);
    setHasMoreSlots(false);
  }, [consultantId]);

  useEffect(() => {
    if (!selectedDate) return; // prevents triggering with undefined

    // Out-of-order response guard (TCX-793). A slow fetch for date A can
    // resolve AFTER the user has switched to date B and B has already
    // succeeded/failed. Committing A's state then would repopulate stale slots
    // under date B (or undo B's error-reset). We tag this effect run as the
    // "latest" request via `active`; any prior run's cleanup flips its own
    // `active` to false, so a superseded response commits NOTHING — not the
    // success state, not the error-reset, not setLoadingSlots(false). The
    // AbortController additionally cancels the in-flight request so we don't
    // pay for a response we'll ignore.
    let active = true;
    const controller = new AbortController();
    // Invalidate any in-flight handleLoadMore for the previous date/tz/consultant.
    requestGenerationRef.current += 1;
    // Clear a Load More that was in flight for the previous selection: its
    // generation is now stale so its finally won't reset this flag, which would
    // otherwise leave the new date's "Load More" button stuck spinning/disabled.
    setLoadingMore(false);

    const fetchInitial = async () => {
      setLoadingSlots(true);
      try {
        const res = await fetch(
          `${
            config.VITE_BE_ENDPOINT
          }booking/consultant/${consultantId}/slots?date=${selectedDate}&timezone=${timezone}`,
          { signal: controller.signal },
        );
        // This request has been superseded (date/tz/consultant changed or the
        // component unmounted). Commit nothing so we can't overwrite the
        // latest request's state. TCX-793.
        if (!active) return;
        // Guard against non-2xx responses: a 4xx/5xx error body must not be
        // stored as valid availability (silent empty/garbage calendar). TCX-793.
        if (!res.ok) {
          throw new Error(`Failed to fetch slots (status ${res.status})`);
        }
        const data = await res.json();

        // Re-check after the async json() parse: this run may have been
        // superseded while awaiting the body. TCX-793.
        if (!active) return;

        setAvailability(data);

        const slots: Slot[] =
          data.days?.map((day: { slots: Slot }) => day.slots).flat() || [];
        const fetchDate: dayjs.Dayjs[] = [];
        data.days?.forEach((day: { date: string }) => {
          fetchDate.push(dayjs.tz(day.date, timezone));
        });
        setRenderDate(fetchDate);
        // setAllSlots(slots);
        setAllSlots(dedupeSlots(slots));
        setHasMoreSlots(slots.length === 20);
      } catch (error) {
        // An AbortError is our own intentional cancellation of a superseded
        // request — treat it as a no-op: no toast, no state reset. TCX-793.
        if (
          !active ||
          (error instanceof DOMException && error.name === 'AbortError')
        ) {
          return;
        }
        // Surface the failure AND clear the slot-list UI so the previous
        // successful fetch's slots for a different date/tz can't be shown or
        // selected. Reset to the same "no slots" shape a successful-but-empty
        // fetch would produce: empty slot list, empty renderDate (header falls
        // back to "Select a date"), no "Load More", and empty parent
        // availability so the calendar cells don't highlight the failed date.
        // TCX-793.
        setAllSlots([]);
        setRenderDate([]);
        setHasMoreSlots(false);
        setSelectedSlot(null);
        setAvailability({ days: [] });
        message.error('Failed to fetch slots');
      } finally {
        // Only the latest request may clear the loading state; a superseded
        // request must not flip loadingSlots off under the current date. TCX-793.
        if (active) {
          setLoadingSlots(false);
        }
      }
    };

    fetchInitial();

    // Switching date/timezone/consultant (or unmounting) supersedes the
    // in-flight request: mark this run inactive and abort the fetch. TCX-793.
    return () => {
      active = false;
      controller.abort();
    };
  }, [selectedDate, consultantId, timezone]);
  const handleLoadMore = async () => {
    if (!selectedDate || allSlots.length === 0 || !consultantId) return;
    // Same latest-request-only guard as fetchInitial (TCX-793). Load More
    // appends rather than replaces, so a superseded response would splice the
    // previous date's slots onto the new date's list. Capture the current
    // request generation; if the fetchInitial effect re-runs (date/tz/
    // consultant change) before this resolves, the token no longer matches and
    // we commit nothing.
    const requestGeneration = requestGenerationRef.current;
    setLoadingMore(true);
    try {
      const lastSlot = allSlots[allSlots.length - 1];
      const from = encodeURIComponent(lastSlot.end);
      const res = await fetch(
        `${
          config.VITE_BE_ENDPOINT
        }booking/consultant/${consultantId}/slots?date=${selectedDate}&timezone=${timezone}&from=${from}`,
      );
      // Superseded by a newer date/tz/consultant selection — drop this append.
      if (requestGeneration !== requestGenerationRef.current) return;
      if (!res.ok) {
        throw new Error(`Failed to load more slots (status ${res.status})`);
      }
      const data = await res.json();
      // Re-check after the async json() parse. TCX-793.
      if (requestGeneration !== requestGenerationRef.current) return;
      const newSlots: Slot[] = data.days?.[0]?.slots || [];
      // setAllSlots((prev) => [...prev, ...newSlots]);
      setAllSlots((prev) => dedupeSlots([...prev, ...newSlots]));
      setHasMoreSlots(newSlots.length === 20);
    } catch (error) {
      // Don't toast for a superseded request. TCX-793.
      if (requestGeneration !== requestGenerationRef.current) return;
      message.error('Failed to load more slots');
    } finally {
      // Only the latest request may clear the load-more spinner. TCX-793.
      if (requestGeneration === requestGenerationRef.current) {
        setLoadingMore(false);
      }
    }
  };

  const slotsToRender = allSlots;

  return (
    <Modal
      open={visible}
      onCancel={isBooking ? undefined : onClose}
      maskClosable={!isBooking}
      keyboard={!isBooking}
      footer={null}
      width={lg ? '80%' : '100%'}
      bodyStyle={{ padding: 0, borderRadius: 24, background: '#fff' }}
      style={{ borderRadius: 24 }}
      title={null}
      centered
      closeIcon={<CloseOutlined style={{ color: '#000' }} />}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: lg ? 'row' : 'column',
          // The right column's own content (label + the now-fixed 420px
          // slot area + footer buttons) is ~590px including its padding —
          // taller than this floor already, so it's what actually pins the
          // row's height today. Kept in sync here so a future change to
          // that column doesn't silently let the calendar side's 5-vs-6-week
          // variance start dominating again (align-items: stretch means the
          // shorter column always grows to match the taller one, so as long
          // as this floor tracks the taller side, total modal height stays
          // constant across states and re-centers consistently).
          minHeight: 590,
          borderRadius: 24,
        }}
      >
        <div
          style={{
            flex: 1,
            padding: 32,
            borderRight: '1px solid #f0f0f0',
            minWidth: 320,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Title level={3} style={{ margin: 0, marginBottom: 8 }}>
            Select a Date & Time
          </Title>
          <Calendar
            className={'book-slot-calendar'}
            fullscreen={false}
            value={calendarValue}
            onSelect={(date) => {
              setSelectedDate(date.format('YYYY-MM-DD'));
            }}
            dateFullCellRender={dateCellRender}
            disabledDate={disabledDate}
            headerRender={calendarHeaderRender}
            key={timezone}
            // Fills the remaining height of the column (a flex column, see
            // the wrapper above) instead of sitting at its own content size
            // with dead space below it whenever the row's height is pinned
            // taller than this column's natural content (e.g. a 5-week
            // month, or the right column's fixed slot-list height). The
            // .book-slot-calendar rules in BookSlotModal.scss make antd's
            // internal header/body/table chain stretch to fill this box too
            // — otherwise they're plain block content that wouldn't grow on
            // their own, leaving the dead space below the last week instead
            // of removing it.
            style={{ flex: 1, marginBottom: 16, background: 'transparent' }}
          />
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, marginBottom: 4, color: '#6b7280' }}>
              Timezone
            </div>
            <Select
              value={timezone}
              onChange={onTimezoneChange}
              style={{ width: '100%' }}
              options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
            />
          </div>
        </div>
        <div
          style={{
            flex: 1,
            padding: 32,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              color: '#6b7280',
              fontWeight: 500,
              marginBottom: 12,
              fontSize: 16,
            }}
          >
            {renderDate?.length > 0
              ? renderDate
                  .map((date) => date.format('dddd, MMMM D'))
                  .join(' and ')
              : 'Select a date'}
          </div>
          {loading || loadingSlots ? (
            <div
              style={{
                // Fixed (not min/max) height, matching the loaded slot-list
                // container below: a min/max range still lets each state
                // settle at a different natural height (the spinner is tiny
                // and sits at the 300 floor; a day with many slots grows
                // toward the 420 ceiling), which is exactly the jump this is
                // meant to prevent. A fixed height removes that degree of
                // freedom entirely.
                height: 420,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
              }}
            >
              <Spin />
              <div style={{ color: '#6b7280', fontSize: 14 }}>
                Fetching available slots...
              </div>
            </div>
          ) : (
            <div
              style={{
                height: 420,
                overflowY: 'auto',
                marginRight: -16,
                paddingRight: 8,
              }}
            >
              <List
                dataSource={slotsToRender}
                renderItem={(slot: Slot) => (
                  <List.Item
                    style={{ padding: 0, marginBottom: 8, border: 'none' }}
                  >
                    <Button
                      block
                      onClick={() => setSelectedSlot(slot)}
                      style={{
                        background:
                          selectedSlot &&
                          selectedSlot.start === slot.start &&
                          selectedSlot.end === slot.end
                            ? '#2A4BC2'
                            : '#f7f8fa',
                        border:
                          selectedSlot &&
                          selectedSlot.start === slot.start &&
                          selectedSlot.end === slot.end
                            ? '2px solid #2A4BC2'
                            : 'none',
                        borderRadius: 12,
                        height: 48,
                        fontWeight: 500,
                        fontSize: 16,
                        color:
                          selectedSlot &&
                          selectedSlot.start === slot.start &&
                          selectedSlot.end === slot.end
                            ? '#fff'
                            : '#222',
                        textAlign: 'left',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {dayjs(slot.start).tz(timezone).format('h:mm A')} -{' '}
                      {dayjs(slot.end).tz(timezone).format('h:mm A')}
                    </Button>
                  </List.Item>
                )}
                locale={{
                  emptyText: selectedDate
                    ? 'No slots available for this day.'
                    : 'Select a date.',
                }}
                style={{ border: 'none', background: 'transparent' }}
              />
              {hasMoreSlots && (
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                  <Button onClick={handleLoadMore} loading={loadingMore} block>
                    Load More
                  </Button>
                </div>
              )}
            </div>
          )}
          {footerNote && (
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 16 }}>
              {footerNote}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              gap: 16,
              marginTop: 32,
            }}
          >
            {/* CustomButton's own CSS sets width: 100%, which — without
                flex: 1 here — makes both buttons claim the full row width
                and flex-shrink evenly with nothing left over for a gap.
                flex: 1 gives each a flex-basis of 0 so they split the row
                (minus the gap above) evenly instead. */}
            <CustomButton
              onClick={onClose}
              type={"default"}
              text={secondaryLabel}
              disabled={isBooking}
              style={{ flex: 1 }}
            />
            <CustomButton
              type={'primary'}
              text={primaryLabel}
              loading={isBooking}
              disabled={!selectedSlot || isBooking}
              style={{ flex: 1 }}
              onClick={() => {
                // Guard against double-submit: while a booking is in flight the
                // button is disabled, and we no longer close the modal here — it
                // closes in the parent's onSuccess (TCX-791).
                if (selectedSlot && !isBooking) {
                  onSlotSelect(selectedSlot);
                }
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default BookSlotModal;
